import { gte, lte } from 'common/PatternMatchingUtilities';
import { getMealPlanRating, getMealPlanRatingCount } from 'common/RatingUtilities';
import { useState } from 'react';
import { useDishModalReviewsQuery } from 'src/gqlReactTypings.generated.d';
import { match } from 'ts-pattern';
import Button, { ButtonType } from 'web-common/src/shared/design-system/components/Button';
import Chip, { ChipColor } from 'web-common/src/shared/design-system/components/Chip';
import { Icon, IconColor } from 'web-common/src/shared/design-system/components/Icon';
import { DishModalReviewCard } from './DishModalReviewCard';

interface DishModalReviewProps {
  foodItemId: number;
  averageRating?: number | null;
  ratingCount?: number | null;
  topPositiveTags?: string[] | null;
}

const INITIAL_REVIEWS = 3;
const LOAD_MORE_INCREMENT = 5;

export const DishModalReviews: React.FC<DishModalReviewProps> = ({
  foodItemId,
  averageRating,
  ratingCount,
  topPositiveTags,
}) => {
  const [displayedReviewsCount, setDisplayedReviewsCount] = useState(INITIAL_REVIEWS);

  const { data: reviewData, fetchMore } = useDishModalReviewsQuery({
    variables: { foodItemId, offset: 0, limit: INITIAL_REVIEWS },
  });

  const reviews = reviewData?.foodItemOrderReviews.reviews ?? [];
  const totalReviews = reviewData?.foodItemOrderReviews.total ?? 0;
  const hasMoreReviews = displayedReviewsCount < totalReviews;
  const hasReviews = reviews.length > 0;
  const hasRatingData = ratingCount && averageRating;

  const handleLoadMore = async () => {
    const currentOffset = displayedReviewsCount;
    await fetchMore({
      variables: {
        offset: currentOffset,
        limit: LOAD_MORE_INCREMENT,
      },
      updateQuery: (previousResult, { fetchMoreResult }) => {
        if (!fetchMoreResult) return previousResult;

        return {
          ...previousResult,
          foodItemOrderReviews: {
            ...previousResult.foodItemOrderReviews,
            reviews: [
              ...(previousResult.foodItemOrderReviews?.reviews || []),
              ...(fetchMoreResult.foodItemOrderReviews?.reviews || []),
            ],
            total: fetchMoreResult.foodItemOrderReviews.total,
          },
        };
      },
    });
    setDisplayedReviewsCount((prev) => prev + LOAD_MORE_INCREMENT);
  };

  const noReviewsMessage = (
    <div className='flex flex-col items-center pb-8 pt-4 text-center'>
      <Icon name='starEmpty' size={24} color={IconColor.EGGPLANT} />
      <p className='mt-4 text-caption-700 uppercase text-eggplant'>Reviews Coming Soon</p>
      <p className='mt-2 px-5 text-body-400 text-eggplant-500'>
        This Shef is new to the neighborhood—be one of the first to try their cooking! Every Shef is taste tested to
        ensure top quality.
      </p>
    </div>
  );

  // Header Right content
  const ratingPercent =
    hasRatingData && ratingCount && averageRating ? getMealPlanRating(ratingCount, averageRating, foodItemId) : 0;
  const displayRatingCount = hasRatingData && ratingCount ? getMealPlanRatingCount(ratingCount, foodItemId) : 0;
  const iconName: 'thumbsUp' | 'thumbsDown' | 'thumbsSide' = match(ratingPercent)
    .with(gte(80), () => 'thumbsUp' as const)
    .with(lte(40), () => 'thumbsDown' as const)
    .otherwise(() => 'thumbsSide' as const);

  const headerRightContent = hasRatingData ? (
    <div className='flex items-center gap-1'>
      <Icon name={iconName} size={16} color={IconColor.EGGPLANT_500} />
      <span className='text-caption-600 font-bold text-eggplant'>{ratingPercent}%</span>
      <span className='text-label-400 text-eggplant-300'>({displayRatingCount})</span>
    </div>
  ) : null;

  return (
    <div>
      {/* Header row */}
      <div className='mb-2 flex items-center justify-between'>
        <p className='text-caption-700-caps text-eggplant'>Dish Reviews</p>
        {headerRightContent}
      </div>

      {/* Top positive tags */}
      {topPositiveTags && topPositiveTags.length > 0 && (
        <div className='mb-4 flex flex-wrap gap-2'>
          {topPositiveTags.map((tag, idx) => (
            <Chip key={idx} color={ChipColor.MILK} copy={tag} />
          ))}
        </div>
      )}

      {/* Review cards or no reviews message */}
      {hasReviews ? (
        <>
          <div className='flex flex-col gap-3'>
            {reviews.map((review, idx) => (
              <DishModalReviewCard key={idx} review={review} foodItemId={foodItemId} />
            ))}
          </div>

          {/* Load more button */}
          {hasMoreReviews && (
            <div className='mt-8 flex justify-center'>
              <Button buttonType={ButtonType.TextLink} onClick={handleLoadMore}>
                Load more reviews
              </Button>
            </div>
          )}
        </>
      ) : (
        noReviewsMessage
      )}
    </div>
  );
};
